<template>
  <div class="event-wrapper" :style="wrapperStyle">

    <transition name="fade">
      <SessionFullModal
        v-if="showSessionFullModal"
        @confirm="closeModalAndRefreshSlots"
      />
    </transition>

    <transition name="page-fade" mode="out-in">

      <ThankYouPage
        v-if="submitted"
        :pages="pages"
        :header="header"
      />

      <component
        v-else-if="loaded"
        :is="currentTemplate"
        :theme="theme"
        :header="header"
        :footer="footer"
        :schema="schema"
        :layout="layout"
        :loading="isSubmitting"
        @submitted="handleFormSubmission"
      />

      <div v-else-if="error" class="error-state">
        <div class="error-card">
          <h3 class="mb-3">Attenzione</h3>
          <p>{{ error }}</p>
          <button @click="fetchEventData(false)" class="btn-malle-outline mt-3">
            Riprova
          </button>
        </div>
      </div>

      <div v-else class="loading-state">
        <div class="loader-container">
          <div class="malle-spinner"></div>
          <p class="loader-text">Caricamento in corso...</p>
        </div>
      </div>

    </transition>
  </div>
</template>

<script>
import { EventService } from '@/services/EventService'
import ThankYouPage from '@/pages/ThankYouPage'

// Layouts
import GlassLayout from '@/templates/GlassLayout'
import HeroLayout from '@/templates/HeroLayout'
import ModernLayout from '@/templates/ModernLayout'
import ScrollPageLayout from '@/templates/ScrollPageLayout'
import SessionFullModal from '@/components/SessionFullModal'

export default {
  name: 'EventPage',
  components: {
    GlassLayout,
    HeroLayout,
    ModernLayout,
    ScrollPageLayout,
    ThankYouPage,
    SessionFullModal
  },

  data () {
    return {
      loaded: false,
      isSubmitting: false,
      submitted: false,
      error: null,
      template: null,
      theme: {},
      header: null,
      footer: null,
      schema: [],
      layout: [],
      pages: null,
      showSessionFullModal: false
    }
  },

  computed: {
    // Mappa i nomi dei template provenienti dal JSON ai componenti Vue
    currentTemplate () {
      const map = {
        'modern-card': 'ModernLayout',
        'scroll-layout': 'ScrollPageLayout',
        'glass-premium': 'GlassLayout',
        'hero-floating': 'HeroLayout'
      }
      return map[this.template] || 'ModernLayout'
    },

    // Applica lo stile visuale globale basato sul tema dell'evento
    wrapperStyle () {
      const bgImage = this.theme?.backgroundImage
        ? { backgroundImage: `url(${this.theme.backgroundImage})` }
        : {}

      return {
        ...bgImage,
        backgroundColor: this.theme?.backgroundColor || '#ffffff',
        color: this.theme?.textColor || '#000000',
        fontFamily: this.theme?.fontFamily || 'inherit',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        transition: 'background 0.5s ease-in-out'
      }
    }
  },

  methods: {
    /**
     * Recupera i dati dell'evento dal server
     * @param {Boolean} isUpdate - Se true, aggiorna i dati silenziosamente (senza loader)
     */
    async fetchEventData (isUpdate = false) {
      const slug = this.$route.params.slug
      if (!slug) return

      if (!isUpdate) this.loaded = false
      this.error = null

      try {
        const data = await EventService.getEventBySlug(slug)

        // Popolamento reattivo dei dati
        this.template = data.template
        this.theme = data.theme
        this.header = data.header
        this.footer = data.footer
        this.schema = data.schema // Normalizzato dal service (fields -> schema)
        this.layout = data.layout
        this.pages = data.pages

        // Aggiorna il titolo del tab del browser
        document.title = data.header?.title?.replace(/<[^>]*>/g, '') || data.name || 'Editions de Parfums Frédéric Malle'

        this.loaded = true
      } catch (err) {
        console.error('Fetch Error:', err)
        if (!isUpdate) {
          this.error = "Impossibile caricare i dati dell'evento. Verifica la connessione."
        }
      }
    },

    /**
     * Gestisce l'invio del modulo
     */
    async handleFormSubmission (formDataObj) {
      if (this.isSubmitting) return
      this.isSubmitting = true
      const slug = this.$route.params.slug

      try {
        const cleanPayload = EventService.preparePayload(formDataObj, this.schema)
        const res = await EventService.registerUser(slug, cleanPayload)

        // Caso: Posti esauriti proprio al momento del click
        if (res?.message === 'session complete') {
          this.showSessionFullModal = true
          this.isSubmitting = false
          return
        }

        // Invio mail asincrono (non blocca la UI)
        EventService.invioMail(res).catch(e => console.warn('Mail non inviata:', e))

        // Mostra la Thank You Page
        this.submitted = true
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        // Gestione errore sessione da exception API
        if (err.response?.data?.message === 'session complete') {
          this.showSessionFullModal = true
        } else {
          alert(err.message || 'Si è verificato un errore durante la registrazione.')
        }
      } finally {
        this.isSubmitting = false
      }
    },

    /**
     * Chiude la modale di errore sessione e ricarica i posti disponibili
     */
    async closeModalAndRefreshSlots () {
      this.showSessionFullModal = false
      await this.fetchEventData(true) // Aggiornamento silenzioso
    }
  },

  watch: {
    // Ricarica i dati se cambiamo evento tramite URL
    '$route.params.slug': {
      immediate: true,
      handler: 'fetchEventData'
    }
  }
}
</script>

<style scoped>
.event-wrapper {
  width: 100%;
  overflow-x: hidden;
  position: relative;
}

/* Stati di caricamento ed Errore */
.loading-state, .error-state {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: rgba(255, 255, 255, 0.8);
}

/* Spinner personalizzato Malle */
.malle-spinner {
  width: 50px;
  height: 50px;
  border: 2px solid #eee;
  border-top: 2px solid #da291c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loader-text {
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 12px;
  font-weight: bold;
}

/* Card Errore */
.error-card {
  max-width: 400px;
  padding: 40px;
  background: white;
  border: 1px solid #da291c;
  border-radius: 4px;
  text-align: center;
}

.btn-malle-outline {
  background: transparent;
  border: 1px solid #da291c;
  color: #da291c;
  padding: 12px 30px;
  border-radius: 50px;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-malle-outline:hover {
  background: #da291c;
  color: white;
}

/* Transizioni */
.page-fade-enter-active, .page-fade-leave-active {
  transition: opacity 0.4s ease;
}
.page-fade-enter, .page-fade-leave-to {
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
